'use client'

import { useState } from 'react'
import { safeHttpHref } from '@entities/fulfillment'
import { type FulfillmentRequestApi } from '@entities/fulfillment-request'
import { DeclineRequestButton } from '@features/fulfillment-request-decline'
import { OfferForm } from '@features/fulfillment-request-offer'
import { Alert, AlertDescription } from '@ui/base/alert'
import { Badge } from '@ui/base/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@ui/base/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ui/base/tabs'
import { typeClass } from '@utils/typography'

import { RequestFiles, RequestMarketplaces } from './request-card'
import { RequestContacts, RequestFacts } from './request-facts'

type ViewerState = NonNullable<FulfillmentRequestApi.ViewerState>

type SectionVariant = 'default' | 'secondary' | 'outline'

/** Разделы входящих заявок: название вкладки, текст пустого раздела и пометка на карточке. */
const sections = [
  {
    state: 'new',
    label: 'Новые',
    empty: 'Новых заявок нет. Как только продавец создаст подходящую — она появится здесь.',
    badge: 'Новая заявка',
    variant: 'default',
  },
  {
    state: 'answered',
    label: 'Я ответил',
    empty: 'Вы ещё не отвечали ни на одну заявку.',
    badge: 'Вы ответили',
    variant: 'secondary',
  },
  {
    state: 'declined',
    label: 'Отклонённые',
    empty: 'Отклонённых заявок нет.',
    badge: 'Вы отклонили',
    variant: 'outline',
  },
] as const satisfies readonly {
  state: ViewerState
  label: string
  empty: string
  badge: string
  variant: SectionVariant
}[]

const sectionOf = (state: ViewerState) => sections.find((item) => item.state === state) ?? sections[0]

const requestState = (request: FulfillmentRequestApi.FulfillmentRequest): ViewerState => request.viewer_state ?? 'new'

const InboxRequestCard = ({
  request,
  fulfillmentId,
  onOfferSent,
  onDeclineChanged,
}: {
  request: FulfillmentRequestApi.FulfillmentRequest
  fulfillmentId: string | null
  onOfferSent: () => void
  onDeclineChanged: (isDeclined: boolean) => void
}) => {
  const state = requestState(request)
  const section = sectionOf(state)

  return (
    <Card role="article" aria-label={request.title}>
      <CardHeader className="grid gap-2 border-b">
        <span className="flex flex-wrap items-center gap-2">
          <Badge variant={section.variant}>{section.badge}</Badge>
          {request.status === 'closed' && <Badge variant="secondary">Продавец закрыл заявку</Badge>}
        </span>
        <CardTitle>{request.title}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-5">
        <RequestFacts request={request} />
        {request.marketplaces.length > 0 && <RequestMarketplaces request={request} />}
        {request.description && (
          <div className="grid gap-1">
            <span className={typeClass.label}>Задача продавца</span>
            <p className={typeClass.body}>{request.description}</p>
          </div>
        )}
        {safeHttpHref(request.product_url) && (
          <div className="grid gap-1">
            <span className={typeClass.label}>Ссылка на товар</span>
            <a
              href={safeHttpHref(request.product_url) ?? undefined}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={`${typeClass.body} underline underline-offset-2`}
            >
              {request.product_url}
            </a>
          </div>
        )}
        {request.files.length > 0 && <RequestFiles requestId={request.id} files={request.files} />}
        <RequestContacts request={request} />
        {/*
          Во входящие приходят только одобренные заявки: непроверенные и возвращённые продавцу
          сервер фулфилменту не отдаёт. Поэтому отвечать можно лишь на открытую, а закрытую видно
          по пометке выше.
        */}
        {request.status === 'open' && (
          <div className="grid gap-4 border-t pt-4">
            <OfferForm request={request} fulfillmentId={fulfillmentId} onSent={onOfferSent} />
            <DeclineRequestButton
              requestId={request.id}
              expectedRevision={request.revision}
              isDeclined={state === 'declined'}
              onDone={onDeclineChanged}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/** Заявки продавцов, разложенные по разделам: новые, отвеченные и отклонённые. */
export const InboxBoard = ({
  requests,
  fulfillmentId,
}: {
  requests: FulfillmentRequestApi.FulfillmentRequest[]
  fulfillmentId: string | null
}) => {
  const [section, setSection] = useState<ViewerState>('new')
  // Ответ и отказ уводят заявку в другой раздел, поэтому подтверждаем действие словами.
  const [notice, setNotice] = useState('')
  const grouped = sections.map((item) => ({
    ...item,
    requests: requests.filter((request) => requestState(request) === item.state),
  }))
  const openSection = (state: ViewerState) => {
    setSection(state)
    setNotice('')
  }

  return (
    <Tabs value={section} onValueChange={(value) => openSection(value as ViewerState)} className="gap-6">
      {notice && (
        <Alert>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}
      <TabsList>
        {grouped.map((item) => (
          <TabsTrigger key={item.state} value={item.state}>
            {item.label}
            <Badge variant={item.state === 'new' ? 'default' : 'secondary'}>{item.requests.length}</Badge>
          </TabsTrigger>
        ))}
      </TabsList>
      {grouped.map((item) => (
        <TabsContent key={item.state} value={item.state} className="grid gap-4">
          {item.requests.length === 0 ? (
            <p className={`${typeClass.body} text-muted-foreground`}>{item.empty}</p>
          ) : (
            item.requests.map((request) => (
              <InboxRequestCard
                key={request.id}
                request={request}
                fulfillmentId={fulfillmentId}
                onOfferSent={() => {
                  setSection('answered')
                  setNotice('Предложение отправлено продавцу — заявка теперь в разделе «Я ответил».')
                }}
                onDeclineChanged={(isDeclined) =>
                  setNotice(
                    isDeclined
                      ? 'Заявка отклонена — она в разделе «Отклонённые», вернуть её можно там же.'
                      : 'Заявка снова в разделе «Новые».'
                  )
                }
              />
            ))
          )}
        </TabsContent>
      ))}
    </Tabs>
  )
}
